import { useCallback, useRef, useState, type TouchEvent } from "react"

const DEFAULT_THRESHOLD = 50

export type UseSwipeOptions = {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  threshold?: number
}

/**
 * Returns touch handlers for horizontal swipe (e.g. photo carousel) on touch devices.
 * Use on the carousel container; onSwipeLeft = go to next, onSwipeRight = go to previous.
 */
export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = DEFAULT_THRESHOLD,
}: UseSwipeOptions) {
  const startX = useRef<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      startX.current = e.touches[0].clientX
      setIsDragging(true)
    },
    []
  )

  const onTouchMove = useCallback(() => {
    // Optional: could track move for visual feedback
  }, [])

  const onTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (startX.current == null) return
      const endX = e.changedTouches[0].clientX
      const delta = endX - startX.current
      startX.current = null
      setIsDragging(false)
      if (Math.abs(delta) < threshold) return
      if (delta < 0) onSwipeLeft?.()
      else onSwipeRight?.()
    },
    [onSwipeLeft, onSwipeRight, threshold]
  )

  return { onTouchStart, onTouchMove, onTouchEnd, isDragging }
}
